import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import API from "../../api";
import ConfirmDialog from "../../components/ConfirmDialog";

const Container = styled(Box)({
  padding: 16,
});

export default function BankManagement() {
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    ifsc: "",
    branchName: "",
    address: "",
    city: "",
    state: "",
    stdCode: "",
    phone: "",
  });
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [file, setFile] = useState(null);

  const show = (message, severity = "error") => {
    setSnack({ open: true, message, severity });
  };

  const load = async () => {
    try {
      const { data } = await API.get("/master/banks");
      setBanks(data);
    } catch {
      show("Failed to load banks");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = () => {
    setEditing(null);
    setForm({
      name: "",
      ifsc: "",
      branchName: "",
      address: "",
      city: "",
      state: "",
      stdCode: "",
      phone: "",
    });
    setOpen(true);
  };

  const handleEdit = (b) => {
    setEditing(b);
    setForm({ ...b });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await API.put(`/master/banks/${editing._id}`, form);
        show("Updated bank", "success");
      } else {
        await API.post("/master/banks", form);
        show("Created bank", "success");
      }
      load();
      setOpen(false);
    } catch (err) {
      show(err.response?.data?.message || "Save failed");
    }
  };

  const handleDeleteClick = (b) => {
    setConfirm({ open: true, id: b._id, name: b.branchName });
  };
  const confirmDelete = async () => {
    try {
      await API.delete(`/master/banks/${confirm.id}`);
      show("Deleted bank", "success");
      load();
    } catch {
      show("Delete failed");
    } finally {
      setConfirm({ open: false, id: null, name: "" });
    }
  };
  const cancelDelete = () => setConfirm({ open: false, id: null, name: "" });

  const handleFileChange = (e) => setFile(e.target.files[0]);
  const handleBulkUpload = async () => {
    if (!file) return show("Select a CSV file");
    const data = new FormData();
    data.append("file", file);
    try {
      const res = await API.post("/master/banks/bulk", data);
      show(`Inserted ${res.data.inserted} records`, "success");
      load();
    } catch (err) {
      show(err.response?.data?.message || "Bulk upload failed");
    }
  };

  return (
    <Container>
      <Typography variant="h5" gutterBottom>
        Bank Management
      </Typography>

      <Box mb={2}>
        <Button variant="contained" onClick={handleAdd} sx={{ mr: 2 }}>
          Add Bank
        </Button>
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          style={{ display: "inline-block", verticalAlign: "middle" }}
        />
        <Button variant="outlined" onClick={handleBulkUpload} sx={{ ml: 1 }}>
          Bulk Upload
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>IFSC</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>STD Code</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banks.map((b) => (
              <TableRow key={b._id}>
                <TableCell>{b.name}</TableCell>
                <TableCell>{b.ifsc}</TableCell>
                <TableCell>{b.branchName}</TableCell>
                <TableCell>{b.city}</TableCell>
                <TableCell>{b.state}</TableCell>
                <TableCell>{b.stdCode}</TableCell>
                <TableCell>{b.phone}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() => handleEdit(b)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(b)}
                  >
                    <DeleteForeverIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editing ? "Edit Bank" : "Add Bank"}</DialogTitle>
        <DialogContent>
          {[
            { label: "Bank Name", key: "name" },
            { label: "IFSC Code", key: "ifsc" },
            { label: "Branch Name", key: "branchName" },
            { label: "Address", key: "address" },
            { label: "City", key: "city" },
            { label: "State", key: "state" },
            { label: "STD Code", key: "stdCode" },
            { label: "Phone Number", key: "phone" },
          ].map(({ label, key }) => (
            <TextField
              key={key}
              fullWidth
              size="small"
              margin="dense"
              label={label}
              value={form[key]}
              onChange={(e) =>
                setForm((f) => ({ ...f, [key]: e.target.value }))
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <ConfirmDialog
        open={confirm.open}
        title="Confirm Delete Bank"
        content={`Delete branch “${confirm.name}”? This cannot be undone.`}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Container>
  );
}
